import httpClient, { ApiResponseType } from 'packages/http_client';
import { unwrap } from 'packages/venture_builder_repository/api';
import { ListApiResponseType, ManagedStartupWalletApiType } from 'packages/venture_builder_repository/types';
import { ResponseRequestTypePayload, ResponseServiceRequestType, ServiceRequestType } from './types';

type RequestWallet = ManagedStartupWalletApiType & { ventureBuilderManagedStartupID: number };

export async function getVentureBuilderRequestWallet(startupId: number, scoped = false): Promise<RequestWallet> {
  if (!Number.isSafeInteger(startupId) || startupId < 1) throw new Error('Startup wallet is unavailable');
  const response = await httpClient.get<ApiResponseType<ListApiResponseType<RequestWallet>>>('/api/d/walletamountutilization', {
    params: { venture_builder_managed_startup_id: startupId, $limit: 2 },
    ...(scoped ? { headers: { 'X-VentureBuilder-Context': String(startupId) } } : {}),
  });
  const { result } = unwrap(response);
  const wallet = result?.[0];
  if (result?.length !== 1 || !wallet?.id || wallet.ventureBuilderManagedStartupID !== startupId
    || ![wallet.balance, wallet.totalWalletAmount, wallet.blockedAmount, wallet.consumedAmount]
      .every(Number.isFinite)) {
    throw new Error('Unable to verify this startup wallet. Retry before approving');
  }
  return wallet;
}

export const ventureBuilderRequestSender = (request?: ServiceRequestType | null): string | undefined => {
  if (!request?.ventureBuilderManagedStartupId) return undefined;
  const startup = request.ventureBuilderManagedStartup;
  if (!startup || startup.id !== request.ventureBuilderManagedStartupId) {
    return `VB startup #${request.ventureBuilderManagedStartupId}`;
  }
  const contact = startup.email ? ` (${startup.email})` : '';
  return `${startup.name || `Startup #${startup.id}`}${contact} — Venture Builder #${startup.ventureBuilderId}`;
};

export const loadVentureBuilderRequestSenders = async (rows: ResponseRequestTypePayload[]) => {
  // Aggregate /request only preloads ServiceRequest, not its managed startup.
  // Fetch that relationship in one batch, using the current admin/provider permissions.
  const ids = rows.filter(({ serviceRequest: request }) => request?.ventureBuilderManagedStartupID
    && request.ventureBuilderManagedStartup?.id !== request.ventureBuilderManagedStartupID)
    .map(({ serviceRequest }) => serviceRequest.id);
  if (!ids.length) return rows;
  try {
    const response = await httpClient.get<ApiResponseType<ListApiResponseType<ResponseServiceRequestType>>>('/api/d/servicerequest', { params: { id__in: ids.join(','), $preload: 1, $limit: ids.length } });
    const { result } = unwrap(response);
    return rows.map((row) => {
      const loaded = result?.find((request) => request.id === row.serviceRequest?.id
        && request.ventureBuilderManagedStartupID === row.serviceRequest.ventureBuilderManagedStartupID);
      return loaded ? { ...row, serviceRequest: loaded } : row;
    });
  } catch {
    // Keep the known owner label ("VB startup #22") if optional name enrichment fails.
    // A failed lookup must not cancel the shared request saga.
    return rows;
  }
};
